<template>
  <PageLayoutWrapper page="programs/_id" :props="{...$props, program }">
    <article v-once ref="content">
      <span v-if="enablePrintDocuments && program.id" class="pl-2 link text-underline flex items-center float-right" @click="() => downloadDocument(program.id)">
        <BaseIcon
          v-if="isDownloadingDocument"
          icon="spinner"
          class="spinner inline-block mr-2"
        />
        <h2>Download as PDF</h2>
      </span>
      <h1 class="pb-2 mb-0">{{programDisplayHeading}}</h1>

      <PageTemplateRenderer :content="program" type="program" />
      <!-- The following is hardcoded for demo purposes  -->
      <template v-if="showProgramMaps">
        <h2>Program Map</h2>
        <div
          v-for="(term, index) of programMap"
          :key="`programMap-${index}`"
        >
          <h5 class="body-font">{{ term.termName }}</h5>
          <p>Total Credits: {{ term.totalCredits }}</p>
          <table class="ui-font" style="width:100%">
            <thead>
              <tr>
                <th>Course Code</th>
                <th>Course Name</th>
                <th>Credits</th>
                <!-- <th>Min Grade.</th> -->
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(course, courseIndex) of term.courses"
                :key="'course-' + courseIndex + term.termName"
              >
                <td>{{ course.code }}</td>
                <td>{{ courseNameWithFallbacks(course) }}</td>
                <td>{{ course.credits }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </article>
  </PageLayoutWrapper>
</template>

<script>
import { mapState } from 'vuex'
import PageLayoutWrapper from '@/components/PageLayoutWrapper'
import pageLayout from '@/mixins/pageLayoutMixin'
import PageTemplateRenderer from '@/components/PageTemplateRenderer'
import ProgramsService from '@/services/programs'
import { generateProgramLabel } from '@/utils/dataLabels'
import {
  courseNameWithFallbacks,
  programNameWithFallbacks,
} from '@/utils/entityNames'
import useInterpolation from '@/composables/useInterpolation'
import useFeatureFlags from '@/composables/useFeatureFlags'
import useDocumentsDownload from '@/composables/useDocumentsDownload'
import { TITLE_INTERPOLATIONS, PRINT_DOCUMENTS } from '@/constants/featureFlags'
import { expandCustomFieldsIntoRootObject } from '@/utils/generalUtils'
import useMeta from '@/composables/useMeta'

const PROGRAM_MAP = [
  {
    termName: 'Semester 1 (Freshman Year)',
    totalCredits: '13',
    courses: [
      {
        code: 'ECON120',
        name: 'Intro To Economics',
        credits: 3,
        minGrade: 'C',
      },
      {
        code: 'ECON200',
        name: 'Principles Of Microeconomics',
        credits: 3,
        minGrade: 'C',
      },
      {
        code: 'MATH150',
        name: 'Multivariable Calculus',
        credits: 3,
        minGrade: 'C',
      },
      {
        code: 'ECON250',
        name: 'Econometrics',
        credits: 4,
        minGrade: 'C',
      },
    ],
  },
  {
    termName: 'Semester 2 (Freshman Year)',
    totalCredits: '12',
    courses: [
      {
        code: 'MATH180',
        name: 'Linear Algebra',
        credits: 3,
        minGrade: 'C',
      },
      {
        code: 'ECON200',
        name: 'Principles Of Macroeconomics',
        credits: 3,
        minGrade: 'C',
      },
      {
        code: 'MATH240',
        name: 'Analytic Geometry',
        credits: 3,
        minGrade: 'C',
      },
      {
        code: 'ECON350',
        name: 'Applied Econometrics',
        credits: 3,
        minGrade: 'C',
      },
    ],
  },
  {
    termName: 'Semester 3 (Sopohmore Year)',
    totalCredits: '14',
    courses: [
      {
        code: 'BIOL140',
        name: 'Intro To Biology',
        credits: 5,
        minGrade: 'C',
      },
      {
        code: 'CHEM120',
        name: 'Intro To Chemistry',
        credits: 3,
        minGrade: 'C',
      },
      {
        code: 'HIST140',
        name: 'European History',
        credits: 3,
        minGrade: 'C',
      },
      {
        code: 'ECON450',
        name: 'Advanced Macroeconomics',
        credits: 3,
        minGrade: 'C',
      },
    ],
  },
  {
    termName: 'Semester 4 (Sopohmore Year)',
    totalCredits: '12',
    courses: [
      {
        code: 'ENGL220',
        name: 'Scientific Writing',
        credits: 3,
        minGrade: 'C',
      },
      {
        code: 'ECON450',
        name: 'Advanced Microeconomics',
        credits: 3,
        minGrade: 'C',
      },
      {
        code: 'MATH400',
        name: 'Fourier Series',
        credits: 3,
        minGrade: 'C',
      },
      {
        code: 'ECON500',
        name: 'Developmental Economics',
        credits: 3,
        minGrade: 'C',
      },
    ],
  },
]

export default {
  components: {
    PageLayoutWrapper,
    PageTemplateRenderer,
  },
  mixins: [pageLayout],
  setup() {
    const { getInterpolatedFieldValue } = useInterpolation()
    const { enableTitleInterpolations, enablePrintDocuments } = useFeatureFlags(
      {
        enableTitleInterpolations: TITLE_INTERPOLATIONS,
        enablePrintDocuments: PRINT_DOCUMENTS,
      }
    )
    const { downloadDocument, isDownloadingDocument } = useDocumentsDownload(
      'program'
    )
    const { catalogWideMetaTitle } = useMeta()
    return {
      courseNameWithFallbacks,
      programNameWithFallbacks,
      getInterpolatedFieldValue,
      enableTitleInterpolations,
      enablePrintDocuments,
      downloadDocument,
      isDownloadingDocument,
      catalogWideMetaTitle,
    }
  },
  head() {
    return {
      title: `${this.program.code ||
        programNameWithFallbacks(this.program)} Program | ${
        this.catalogWideMetaTitle
      }`,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: `${this.program.code ||
            programNameWithFallbacks(this.program)} Program | ${
            this.catalogWideMetaTitle
          }`,
        },
      ],
    }
  },
  computed: {
    ...mapState('settings', ['school']),
    programMap() {
      return PROGRAM_MAP
    },
    showProgramMaps() {
      return this.school && this.school.includes('demo')
    },
    programDisplayHeading() {
      return generateProgramLabel(this.program, this.school, {
        getInterpolatedFieldValue: this.enableTitleInterpolations
          ? this.getInterpolatedFieldValue
          : undefined,
      })
    },
  },
  async asyncData({ params, query, $axios, $bugsnag, redirect, store }) {
    const api = new ProgramsService($axios, store)
    const { effectiveDatesRange } = query;
    const enableHistoricalRevisions = store.state?.settings?.settings?.enableHistoricalRevisions;
    try {
      const programGroupId = decodeURIComponent(params.id)
      
      const programParams = { 
        includeMappedDocumentItems: true,
        ...(enableHistoricalRevisions && effectiveDatesRange ? { effectiveDatesRange } : {}) // to disable historical revisions if turned of for catalog 
      }

      const [program, rootRevision, { terms }, { semesterNames }] = await Promise.all([
        api.getProgram(programGroupId, programParams),
        api.getProgram(programGroupId, { 
          includeMappedDocumentItems: false, 
          columns: 'id,programGroupId,effectiveStartDate,effectiveEndDate' 
        }),
        $axios.$get(`/${store.state.settings.school}/general/terms`),
        $axios.$get(`/${store.state.settings.school}/general/semesterNames`),
      ])

      if (!rootRevision) {
        redirect('/404')
        return
      }

      // Filter out data that is not allowed to be shown in the public catalog
      program.specializations = (program.specializations || []).filter(
        (el) => el.showInCatalog
      )
      program.degreeMaps = (program.degreeMaps || []).filter(
        (el) => el.displayedInCatalog
      )

      program.requisites = program.requisites || {}
      program.requisites.requisitesSimple = (
        program.requisites.requisitesSimple || []
      ).filter((el) => el.showInCatalog)
      program.requisites.requisitesAdvanced = (
        program.requisites.requisitesAdvanced || []
      ).filter((el) => el.showInCatalog)
      program.requisites.requisitesFreeform = (
        program.requisites.requisitesFreeform || {}
      ).showInCatalog
        ? program.requisites.requisitesFreeform
        : {}

      return {
        program: {
          ...program.customFields,
          ...program,
          instructionalMethods: expandCustomFieldsIntoRootObject(program.instructionalMethods || []),
        },
        terms,
        semesterNames,
        rootRevision
      }
    } catch (e) {
      console.error(e)
      $bugsnag.notify(e)
      redirect('/404')
    }
  },
  watchQuery: ['effectiveDatesRange'],
  created() {
    this.$store.commit(
      'page/SET_CURRENT_PAGE_LABEL',
      this.programDisplayHeading
    )
  },
  provide() {
    return {
      terms: this.terms,
      semesterNames: this.semesterNames,
      rootRevision: this.rootRevision,
      currentRevision: this.program,
    }
  },
}
</script>

<style lang="css" scoped>
.nuxt-link-active {
  @apply border-l border-t border-r rounded-t;
}
.link {
  cursor: pointer;
}
.link h2 {
  font-size: 16px;
}
</style>
